import '../styles.css';
import { App } from './ui/App';
import { mountHome } from './ui/home';
import { editorPath, go, onRoute, parse, rewrite, type Route } from './router';
import { memoryLibrary, openLibrary } from './state/library';

const root = document.getElementById('app');
if (!root) throw new Error('missing #app root element');
const library = typeof indexedDB === 'undefined' ? memoryLibrary() : openLibrary();

interface Mounted {
  destroy(): Promise<void> | void;
}

let current: Mounted | null = null;
let app: App | null = null;
/** A photo dropped on the home page, opened by the editor that mounts next. */
let handoff: File | null = null;
let generation = 0;

async function mountEditor(route: Extract<Route, { name: 'editor' }>): Promise<Mounted> {
  const editor = new App(root!, { library });
  app = editor;
  window.__stringArtApp = editor;
  const stop = editor.store.subscribe((s, p) => {
    if (s.design.id && s.design.id !== p.design.id) rewrite(editorPath(s.design.id));
  });
  await editor.boot();
  if (route.id) {
    if (!(await editor.openDesign(route.id))) rewrite(editorPath(null));
  } else if (handoff) {
    const file = handoff;
    handoff = null;
    void editor.loadImageFile(file);
  } else if (route.sample) {
    void editor.loadSample(route.sample);
  }
  return {
    async destroy() {
      stop();
      await editor.flushSave();
      editor.dispose();
      if (app === editor) {
        app = null;
        window.__stringArtApp = undefined;
      }
    },
  };
}

async function route(): Promise<void> {
  const token = ++generation;
  const r = parse();
  const previous = current;
  current = null;
  await previous?.destroy();
  if (token !== generation) return;
  root!.replaceChildren();
  root!.setAttribute('aria-busy', 'true');
  const mounted = r.name === 'editor' ? await mountEditor(r) : await mountHome(root!, { library, go, onPhoto: (file) => { handoff = file; go(editorPath(null)); } });
  if (token !== generation) {
    await mounted.destroy();
    return;
  }
  current = mounted;
  root!.removeAttribute('aria-busy');
}

function report(message: string): void {
  if (app) app.fail(message);
  else console.error(message);
}
window.addEventListener('error', (e) => report(`Unexpected error: ${e.message}`));
window.addEventListener('unhandledrejection', (e) => report(`Unexpected error: ${e.reason instanceof Error ? e.reason.message : String(e.reason)}`));

onRoute(() => void route());

declare global {
  interface Window {
    __stringArtApp?: App;
  }
}
